/**
 * ข้อมูลและเกณฑ์มาตรฐานระดับความรุนแรงและการตอบสนองต่ออุบัติการณ์ (Severity Level & Incident Response Guide)
 * 
 * มาตรฐานอ้างอิง:
 * 1. ทันตแพทยสภา: Dental Safety Goals & Guidelines (2P Safety DSG 2024)
 * 2. สถาบันรับรองคุณภาพสถานพยาบาล (สรพ. - HA Thailand / Incident Management System)
 * 3. National Coordinating Council for Medication Error Reporting and Prevention (NCC MERP)
 */

export const CLINICAL_SEVERITY_GUIDE = [
  {
    code: 'A',
    name: 'เหตุการณ์ที่อาจก่อให้เกิดความคลาดเคลื่อน',
    englishName: 'Circumstance or events that have capacity to cause error',
    group: 'Near Miss (เหตุการณ์เกือบพลาด)',
    groupCode: 'near_miss',
    color: '#0284c7',
    badgeBg: '#e0f2fe',
    badgeBorder: '#bae6fd',
    score: 1,
    description: 'เหตุการณ์หรือสภาพแวดล้อมที่มีโอกาสก่อให้เกิดความคลาดเคลื่อนได้ แต่ยังไม่เกิดขึ้นจริง',
    action_timeline: 'แก้ไขปรับปรุงเชิงระบบใน 24-48 ชม.',
    action_hours: 48,
    reporting_timeline: 'บันทึกรายงาน รวบรวมสรุปประจำเดือน',
    reporting_hours: 720,
    rca_required: false,
    rca_type: 'ไม่ต้องทำ RCA',
    rca_timeline: 'ไม่ต้องทำ',
    examples: [
      'แสงสว่างบนโคมไฟยูนิตทำฟันไม่เพียงพอ เสี่ยงต่อการมองเห็นไม่ชัดเจน',
      'ซองบรรจุเครื่องมือสเตอไรล์ใกล้หมดอายุวางปะปนกับชุดใหม่',
      'ขวดยาชา 2% Mepivacaine วางติดกับ Lidocaine เสี่ยงหยิบผิดหลอด'
    ]
  },
  {
    code: 'B',
    name: 'เกิดความคลาดเคลื่อนขึ้น แต่ดักจับได้ก่อนถึงผู้ป่วย',
    englishName: 'Error occurred but did not reach the patient',
    group: 'Near Miss (เหตุการณ์เกือบพลาด)',
    groupCode: 'near_miss',
    color: '#0369a1',
    badgeBg: '#e0f2fe',
    badgeBorder: '#7dd3fc',
    score: 2,
    description: 'เกิดความผิดพลาดขึ้นแล้ว แต่ถูกดักจับหรือตรวจพบได้ก่อนที่จะส่งถึงผู้ป่วย',
    action_timeline: 'แก้ไขสับเปลี่ยนทันทีใน 1-2 ชม.',
    action_hours: 2,
    reporting_timeline: 'บันทึกรายงานใน 24-48 ชม.',
    reporting_hours: 48,
    rca_required: false,
    rca_type: 'ไม่ต้องทำ RCA (ทบทวนหน้างาน)',
    rca_timeline: 'ทบทวนหน้างาน',
    examples: [
      'ตรวจพบซองเครื่องมือฉีกขาดหรือแถบซีลหลุดก่อนนำเข้าห้องตรวจ',
      'หยิบยาชาหรือยาปฏิชีวนะผิดหลอด แต่แพทย์ตรวจสอบฉลากซ้ำก่อนฉีด/จ่าย',
      'จัดแฟ้มประวัติหรือฟิล์มเอกซเรย์สลับผู้ป่วย แต่ตรวจพบก่อนเรียกผู้ป่วยขึ้นเตียง'
    ]
  },
  {
    code: 'C',
    name: 'เกิดความคลาดเคลื่อนถึงผู้ป่วย แต่ไม่เกิดอันตราย',
    englishName: 'Error reached patient but did not cause harm',
    group: 'No Harm (ไม่มีอันตราย)',
    groupCode: 'no_harm',
    color: '#15803d',
    badgeBg: '#dcfce7',
    badgeBorder: '#86efac',
    score: 3,
    description: 'ความคลาดเคลื่อนไปถึงผู้ป่วยแล้ว แต่ไม่ก่อให้เกิดอันตรายหรือบาดเจ็บใดๆ ต่อร่างกายและจิตใจ',
    action_timeline: 'ดูแลและตรวจประเมินทันที < 1 ชม.',
    action_hours: 1,
    reporting_timeline: 'รายงานหัวหน้างานภายในเวร (24 ชม.)',
    reporting_hours: 24,
    rca_required: false,
    rca_type: 'ไม่ต้องทำ RCA',
    rca_timeline: 'ไม่ต้องทำ',
    examples: [
      'ผู้ป่วยได้รับน้ำยาบ้วนปากผิดกลิ่น/ผิดสูตร แต่ไม่มีอาการระคายเคือง',
      'ทายาชาเฉพาะที่ผิดชนิด แต่ผู้ป่วยไม่มีประวัติแพ้และไม่มีผลข้างเคียง',
      'หยอดสีตรวจคราบจุลินทรีย์เลอะมุมปากผู้ป่วย เช็ดออกได้ทันทีไม่มีรอยไหม้'
    ]
  },
  {
    code: 'D',
    name: 'เกิดความคลาดเคลื่อนถึงผู้ป่วย ต้องเฝ้าระวังหรือสังเกตอาการ',
    englishName: 'Error reached patient, required monitoring to confirm no harm',
    group: 'No Harm (ไม่มีอันตราย)',
    groupCode: 'no_harm',
    color: '#ca8a04',
    badgeBg: '#fef9c3',
    badgeBorder: '#fde047',
    score: 4,
    description: 'ความคลาดเคลื่อนถึงผู้ป่วย ทำให้ต้องติดตาม สังเกตอาการ หรือตรวจประเมินเพิ่มเติมเพื่อให้มั่นใจว่าไม่เกิดอันตราย',
    action_timeline: 'ปฐมพยาบาลทันที < 30 นาที ติดตามอาการใน 24-48 ชม.',
    action_hours: 0.5,
    reporting_timeline: 'รายงานในเวร (12-24 ชม.)',
    reporting_hours: 24,
    rca_required: false,
    rca_type: 'ไม่ต้องทำ RCA (เสนอทบทวนในคณะกรรมการความเสี่ยง)',
    rca_timeline: 'เสนอทบทวนกรรมการ',
    examples: [
      'กระจกส่องปาก (Mouth mirror) กดมุมปากเกิดรอยถลอกตื้น ปฐมพยาบาลทายาและติดตาม',
      'ผู้ป่วยมีอาการใจสั่นเล็กน้อยหลังฉีดยาชา ให้พักผ่อน วัดความดันซ้ำจนกลับมาปกติ',
      'น้ำยาล้างคลองรากฟัน (NaOCl) ซึมขอบแผ่นยางกันน้ำลายเล็กน้อย ล้างน้ำเกลือทันทีไม่ระคายเคือง'
    ]
  },
  {
    code: 'E',
    name: 'เกิดอันตรายชั่วคราว ต้องให้การบำบัดรักษา/หัตถการแก้ไข',
    englishName: 'Temporary harm requiring intervention',
    group: 'Harm (เกิดอันตราย)',
    groupCode: 'harm',
    color: '#ea580c',
    badgeBg: '#ffedd5',
    badgeBorder: '#fdba74',
    score: 5,
    description: 'ความคลาดเคลื่อนส่งผลให้เกิดอันตรายชั่วคราวแก่ผู้ป่วย ต้องให้การบำบัดรักษา หรือทำหัตถการแก้ไขเพิ่มเติม',
    action_timeline: 'รักษาแก้ไขทันที < 1-2 ชม.',
    action_hours: 2,
    reporting_timeline: 'รายงานหัวหน้ากลุ่มงานทันตกรรมใน 2-4 ชม. / บันทึกรายงานใน 24 ชม.',
    reporting_hours: 4,
    rca_required: true,
    rca_type: 'RCA ระดับหน่วยงาน (Mini-RCA)',
    rca_timeline: 'ภายใน 7-14 วัน',
    examples: [
      'ตะไบขยายคลองรากฟันหักคาในรากฟัน (Broken endodontic file) ต้องทำหัตถการคีบออกหรือบายพาส',
      'หัวกรอความเร็วสูงสะบัดโดนลิ้น/กระพุ้งแก้ม มีแผลฉีกขาดต้องเย็บแผล (Suture)',
      'แผลไหม้ความร้อนที่ริมฝีปากจากด้ามหัวกรอสะสมความร้อน (Handpiece burn)',
      'ฟันข้างเคียงบิ่นชำรุดขณะใช้คีมถอนฟัน ต้องทำการอุดบูรณะแก้ไข'
    ]
  },
  {
    code: 'F',
    name: 'เกิดอันตรายชั่วคราว ต้อง Admit นอน รพ. หรืออยู่นานขึ้น',
    englishName: 'Temporary harm requiring hospitalization or prolonged stay',
    group: 'Harm (เกิดอันตราย)',
    groupCode: 'harm',
    color: '#c2410c',
    badgeBg: '#fed7aa',
    badgeBorder: '#fb923c',
    score: 6,
    description: 'ความคลาดเคลื่อนส่งผลให้เกิดอันตรายชั่วคราวที่ทำให้ผู้ป่วยต้องรับไว้รักษาในโรงพยาบาล (Admit) หรือยืดระยะเวลานอนโรงพยาบาลนานขึ้น',
    action_timeline: 'ส่งต่อแพทย์/ER ทันที < 30-60 นาที',
    action_hours: 1,
    reporting_timeline: 'รายงานด่วนทางวาจา/โทรศัพท์ต่อผู้บริหารทันที (< 2 ชม.) / บันทึกทางการใน 24 ชม.',
    reporting_hours: 2,
    rca_required: true,
    rca_type: 'Full RCA (ระดับโรงพยาบาล)',
    rca_timeline: 'ภายใน 7 วัน',
    examples: [
      'ชิ้นส่วนครอบฟันชั่วคราว/หัวกรอหลุดลงทางเดินหายใจ (Foreign body aspiration) ต้องนำส่ง ER ส่องกล้องคีบออกและ Admit',
      'เลือดออกไม่หยุดหลังผ่าตัดถอนฟันคุดจนเกิดภาวะ Hypovolemia ต้องรับไว้รักษาใน รพ.',
      'การติดเชื้อรุนแรงแพร่กระจายไปยังช่องว่างใต้คาง (Ludwig\'s Angina) หลังถอนฟัน'
    ]
  },
  {
    code: 'G',
    name: 'เกิดอันตรายถาวร พิการ หรือสูญเสียหน้าที่อวัยวะ',
    englishName: 'Permanent harm / Permanent impairment of body function',
    group: 'Severe Harm / Sentinel Event (รุนแรงมาก/วิกฤต)',
    groupCode: 'severe_harm',
    color: '#dc2626',
    badgeBg: '#fee2e2',
    badgeBorder: '#fca5a5',
    score: 7,
    description: 'ความคลาดเคลื่อนส่งผลให้ผู้ป่วยได้รับอันตรายถาวร สูญเสียอวัยวะ หรือสูญเสียการทำหน้าที่ของร่างกายอย่างถาวร',
    action_timeline: 'รักษาและส่งต่อผู้เชี่ยวชาญทันที < 1 ชม.',
    action_hours: 1,
    reporting_timeline: 'รายงานด่วนต่อผู้อำนวยการ รพ. ทันที (< 1-2 ชม.)',
    reporting_hours: 2,
    rca_required: true,
    rca_type: 'Hospital-wide Full RCA',
    rca_timeline: 'ภายใน 3-5 วัน',
    examples: [
      'การบาดเจ็บรุนแรงต่อเส้นประสาท Inferior Alveolar / Lingual Nerve ส่งผลให้ริมฝีปากหรือลิ้นชาถาวร',
      'การผ่าตัดหรือถอนฟันผิดซี่ถาวรโดยไม่สามารถปลูกถ่ายทดแทนได้ (Wrong tooth extraction)',
      'การฉีดสารเคมีรุนแรงผิดพลาดเข้าหลอดเลือดหรือเนื้อเยื่อส่งผลให้เนื้อตายบริเวณกว้าง'
    ]
  },
  {
    code: 'H',
    name: 'เกิดอันตรายวิกฤติต้องช่วยชีวิตเร่งด่วน (Life-threatening)',
    englishName: 'Intervention required to sustain life',
    group: 'Severe Harm / Sentinel Event (รุนแรงมาก/วิกฤต)',
    groupCode: 'severe_harm',
    color: '#b91c1c',
    badgeBg: '#fecaca',
    badgeBorder: '#f87171',
    score: 8,
    description: 'ความคลาดเคลื่อนส่งผลให้ผู้ป่วยอยู่ในภาวะวิกฤติต้องได้รับการกู้ชีพ (CPR) หรือหัตถการช่วยชีวิตเร่งด่วน',
    action_timeline: 'กู้ชีพฉุกเฉินทันที (CPR / Code Emergency 0-5 นาที)',
    action_hours: 0.08,
    reporting_timeline: 'รายงานด่วนผู้อำนวยการ รพ. ทันที (< 1 ชม.)',
    reporting_hours: 1,
    rca_required: true,
    rca_type: 'Full RCA (เร่งด่วนระดับองค์กร)',
    rca_timeline: 'ภายใน 1-3 วัน',
    examples: [
      'ภาวะแพ้ยาชาหรือยาปฏิชีวนะรุนแรงชนิด Anaphylactic shock หยุดหายใจบนยูนิตทำฟัน',
      'สิ่งแปลกปลอมอุดกั้นทางเดินหายใจส่วนบนเฉียบพลัน (Complete airway obstruction) ต้องเปิดทางเดินหายใจฉุกเฉิน',
      'ภาวะแทรกซ้อนทางระบบหัวใจและหลอดเลือดเฉียบพลัน (Cardiac arrest) ขณะทำหัตถการ'
    ]
  },
  {
    code: 'I',
    name: 'ความคลาดเคลื่อนเป็นเหตุให้ผู้ป่วยเสียชีวิต (Death)',
    englishName: 'Sentinel Event: Patient death caused by error',
    group: 'Severe Harm / Sentinel Event (รุนแรงมาก/วิกฤต)',
    groupCode: 'severe_harm',
    color: '#7f1d1d',
    badgeBg: '#450a0a',
    textColor: '#ffffff',
    badgeBorder: '#991b1b',
    score: 9,
    description: 'ความคลาดเคลื่อนเป็นสาเหตุโดยตรงหรือโดยอ้อมที่ทำให้ผู้ป่วยเสียชีวิต (Sentinel Event สูงสุด)',
    action_timeline: 'กู้ชีพขั้นสูงและการดำเนินการตามกฎหมายทันที',
    action_hours: 0,
    reporting_timeline: 'รายงานผู้บริหารสูงสุด / สสจ. / ทันตแพทยสภา ทันที (< 1-2 ชม.)',
    reporting_hours: 2,
    rca_required: true,
    rca_type: 'Critical Sentinel Event RCA',
    rca_timeline: 'ภายใน 24-48 ชม.',
    examples: [
      'ผู้ป่วยเสียชีวิตจากภาวะแทรกซ้อนรุนแรงขณะทำหัตถการ หรือหลังการให้ยาระงับความรู้สึก',
      'ผู้ป่วยเสียชีวิตจากการสำลักสิ่งแปลกปลอมที่ไม่สามารถเปิดทางเดินหายใจได้ทันท่วงที'
    ]
  }
];

export const NON_CLINICAL_SEVERITY_GUIDE = [
  {
    code: '1',
    name: 'เล็กน้อยมาก (Very Low)',
    englishName: 'Very Low severity',
    group: 'ระดับ 1-2 เล็กน้อย',
    groupCode: 'low',
    color: '#16a34a',
    badgeBg: '#dcfce7',
    badgeBorder: '#86efac',
    score: 1,
    description: 'เหตุการณ์เล็กน้อย ไม่กระทบต่อการให้บริการ ไม่มีความเสียหายต่อทรัพย์สินหรือบุคลากร',
    action_timeline: 'แก้ไขใน 24-48 ชม.',
    action_hours: 48,
    reporting_timeline: 'บันทึกรายงานสรุปประจำเดือน',
    reporting_hours: 720,
    rca_required: false,
    rca_type: 'ไม่ต้องทำ RCA',
    rca_timeline: 'ไม่ต้องทำ',
    examples: [
      'หลอดไฟบริเวณทางเดินกระพริบ',
      'กระดาษทิชชูในห้องน้ำหมดชั่วคราว',
      'เอกสารเวชระเบียนสะกดชื่อผิดเล็กน้อยแก้ไขได้ทันที'
    ]
  },
  {
    code: '2',
    name: 'เล็กน้อย (Low)',
    englishName: 'Low severity',
    group: 'ระดับ 1-2 เล็กน้อย',
    groupCode: 'low',
    color: '#0284c7',
    badgeBg: '#e0f2fe',
    badgeBorder: '#7dd3fc',
    score: 2,
    description: 'เกิดความขัดข้องเล็กน้อย แก้ไขได้ทันที กระทบการทำงานสั้นๆ ทรัพย์สินเสียหายเล็กน้อย',
    action_timeline: 'แก้ไขใน 4-8 ชม.',
    action_hours: 8,
    reporting_timeline: 'รายงานใน 24 ชม.',
    reporting_hours: 24,
    rca_required: false,
    rca_type: 'ไม่ต้องทำ RCA',
    rca_timeline: 'ไม่ต้องทำ',
    examples: [
      'เครื่องปรับอากาศห้องตรวจมีน้ำหยดเล็กน้อย',
      'คอมพิวเตอร์จุดเวชระเบียนค้าง รีสตาร์ทแล้วทำงานต่อได้',
      'ถุงขยะติดเชื้อเต็มล้นชั่วคราว จัดการเปลี่ยนถุงใหม่ได้ทันที'
    ]
  },
  {
    code: '3',
    name: 'ปานกลาง (Medium)',
    englishName: 'Medium severity',
    group: 'ระดับ 3 ปานกลาง',
    groupCode: 'medium',
    color: '#ea580c',
    badgeBg: '#ffedd5',
    badgeBorder: '#fdba74',
    score: 3,
    description: 'อุปกรณ์ชำรุดเสียหาย เครื่องมือหยุดทำงานชั่วคราว หรือบุคลากรบาดเจ็บเล็กน้อย (ปฐมพยาบาลได้)',
    action_timeline: 'แก้ไขใน 1-4 ชม.',
    action_hours: 4,
    reporting_timeline: 'รายงานใน 12-24 ชม.',
    reporting_hours: 24,
    rca_required: true,
    rca_type: 'RCA ระดับหน่วยงาน (Mini-RCA)',
    rca_timeline: 'ภายใน 7-14 วัน',
    examples: [
      'ผู้ช่วยทันตแพทย์ถูกเข็มหรือของมีคมตำ (Needle Stick Injury) ปฐมพยาบาลและเจาะเลือดตรวจ',
      'เครื่องนึ่ง Autoclave ขัดข้อง 1 เครื่อง ต้องส่งต่ออุปกรณ์ไปนึ่งเครื่องอื่น',
      'น้ำประปาในคลินิกหยุดไหลชั่วคราว ต้องใช้น้ำสำรอง'
    ]
  },
  {
    code: '4',
    name: 'รุนแรง (High)',
    englishName: 'High severity',
    group: 'ระดับ 4-5 รุนแรง',
    groupCode: 'high',
    color: '#dc2626',
    badgeBg: '#fee2e2',
    badgeBorder: '#fca5a5',
    score: 4,
    description: 'ทรัพย์สินเสียหายมูลค่าสูง ระบบหลักหยุดชะงัก หรือบุคลากรบาดเจ็บจนต้องหยุดงาน/รับการรักษา',
    action_timeline: 'แก้ไขฉุกเฉินใน 1 ชม.',
    action_hours: 1,
    reporting_timeline: 'รายงานผู้บริหารใน 2 ชม.',
    reporting_hours: 2,
    rca_required: true,
    rca_type: 'Full RCA (ระดับโรงพยาบาล)',
    rca_timeline: 'ภายใน 7 วัน',
    examples: [
      'ระบบลม Suction หรือ Air Compressor หลักดับทั้งคลินิก ไม่สามารถให้บริการผู้ป่วยได้',
      'ไฟฟ้าดับและระบบเครื่องกำเนิดไฟฟ้าสำรองไม่ทำงาน',
      'สารเคมีอันตรายรั่วไหลในห้องล้างเครื่องมือ บุคลากรสูดดมต้องส่งห้องฉุกเฉิน'
    ]
  },
  {
    code: '5',
    name: 'รุนแรงวิกฤติ / ภัยพิบัติ (Catastrophic)',
    englishName: 'Catastrophic severity',
    group: 'ระดับ 4-5 รุนแรง',
    groupCode: 'high',
    color: '#7f1d1d',
    badgeBg: '#450a0a',
    textColor: '#ffffff',
    badgeBorder: '#991b1b',
    score: 5,
    description: 'เกิดความสูญเสียร้ายแรง ภัยพิบัติ บุคลากรบาดเจ็บสาหัส/เสียชีวิต หรือกระทบชื่อเสียงองค์กรอย่างรุนแรง',
    action_timeline: 'ระงับเหตุฉุกเฉินทันที',
    action_hours: 0,
    reporting_timeline: 'รายงานผู้บริหารสูงสุดทันที (< 1-2 ชม.)',
    reporting_hours: 2,
    rca_required: true,
    rca_type: 'Critical Sentinel Event RCA',
    rca_timeline: 'ภายใน 24-48 ชม.',
    examples: [
      'เพลิงไหม้คลินิกหรืออาคารทันตกรรม',
      'ถังแก๊สอัดแรงดันหรือระบบท่อแรงดันสูงระเบิด',
      'บุคลากรหรือเจ้าหน้าที่ประสบอุบัติเหตุรุนแรงเสียชีวิตขณะปฏิบัติงาน'
    ]
  }
];

/**
 * กฎและเกณฑ์สรุปการทำ RCA (Root Cause Analysis Summary Rules)
 */
export const RCA_RULES_SUMMARY = {
  clinicalThreshold: 'ระดับ E ขึ้นไป (E, F, G, H, I)',
  nonClinicalThreshold: 'ระดับ 3 ขึ้นไป (3, 4, 5)',
  standardReference: 'อ้างอิงเกณฑ์ 2P Safety DSG 2024 ทันตแพทยสภา & สรพ. (HA Thailand)',
  summaryPoints: [
    {
      title: 'ระดับความรุนแรงที่ต้องทำ RCA',
      badge: '⚠️ บังคับทำ RCA',
      description: 'ทางคลินิก: Level E, F, G, H, I | ทั่วไป/สิ่งแวดล้อม: Level 3, 4, 5'
    },
    {
      title: 'เกณฑ์เวลาการรายงานด่วน (Urgent Reporting)',
      badge: '⏱️ รายงานด่วน',
      description: 'ระดับวิกฤต (G, H, I / 4, 5) รายงานผู้บริหารทันที < 1-2 ชม. | ระดับอันตราย (E, F / 3) รายงานใน 2-4 ชม.'
    },
    {
      title: 'กรอบเวลาการทำ RCA ให้แล้วเสร็จ',
      badge: '📋 RCA Timelines',
      description: 'Mini-RCA (E, 3): 7-14 วัน | Full RCA (F, 4): 7 วัน | Hospital-wide/Sentinel (G, H, I, 5): 1-5 วัน'
    }
  ]
};

/**
 * ฟังก์ชันช่วยดึงข้อมูล Guideline ตาม Code และ Domain
 */
export const getSeverityGuide = (code, domain = 'clinical') => {
  if (!code) return null;
  const codeStr = String(code).trim().toUpperCase();
  const isAlpha = /^[A-I]$/i.test(codeStr);
  const targetList = (domain === 'clinical' || domain === 'cssd' || isAlpha)
    ? CLINICAL_SEVERITY_GUIDE
    : NON_CLINICAL_SEVERITY_GUIDE;

  const found = targetList.find((item) => item.code.toUpperCase() === codeStr);
  if (found) return found;

  return (
    CLINICAL_SEVERITY_GUIDE.find((item) => item.code.toUpperCase() === codeStr) ||
    NON_CLINICAL_SEVERITY_GUIDE.find((item) => item.code.toUpperCase() === codeStr) ||
    null
  );
};

/**
 * ฟังก์ชันตรวจสอบว่าระดับนี้ต้องทำ RCA หรือไม่
 */
export const isRcaRequired = (code) => {
  const guide = getSeverityGuide(code);
  return Boolean(guide?.rca_required);
};
